import { queryKeys } from '@query/keys'
import { PaginatedQueryResult, usePaginatedQuery } from '@query/usePaginatedQuery'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'
import { useSnackbar } from '@context/SnackbarContext'

import { PageSection } from '@ui/components/PageSection'
import { CirclePlusIcon } from '@ui/icons/CirclePlus.icon'
import { PercentIcon } from '@ui/icons/Percent.icon'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'
import { SummaryItem, TableSummary } from '@ui/v2/TableSummary/TableSummary'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { FilterQueryProps } from '@model/FilterFields'
import { IntroducingBrokerRebateGroupDto } from '@model/IntroducingBrokerRebateGroupDto'
import { NameDto } from '@model/NameDto'

import { ScrollToIds } from '@hooks/useScrollToElementIds'

import { Loading } from '@shared/components/Loading/Loading'
import { ConfirmModal } from '@shared/modals/Confirm.modal'

import { GroupFormValues, GroupModal } from '../Group.modal'
import { useCreateOrUpdateRebateGroup } from '../hooks/useCreateOrUpdateRebateGroup'
import { useIBRebateGroupsSearch } from '../hooks/useIBRebateGroupsSearch'
import { useRebateGroupsOptions } from '../hooks/useRebateGroupsOptions'
import { GroupsPageTable } from './GroupsPageTable'

import styles from './Groups.page.module.scss'

const CA_TRADING_ACCOUNTS_COUNT_FILTER_REGEX =
  /^ca_search_(TradingAccountsCountFrom|TradingAccountsCountTo)$/

export const IBRebatesGroupsPage: FC = () => {
  const { t } = useTranslation()

  const [isFilterVisible, setIsFilterVisible] = useState(false)

  const {
    query: tableQuery,
    sortOptions: tableSortOptions,
    search: tableSearch,
    setSearch: setTableSearch,
    forceRefresh: forceRefreshTable,
  } = useIBRebateGroups()

  const isAnyFilterActive = useMemo(
    () => Object.values(tableSearch || {}).some((value) => !!value),
    [tableSearch]
  )

  const { groupOptions, setSearch: setListSearch } = useIBRebateGroupsSearch()
  const { rebateApprovalTypes, rebatePeriods } = useRebateGroupsOptions()

  const {
    summaryItems,
    forceRefresh: forceRefreshGroupsSummary,
    isLoading: isSummaryLoading,
  } = useIBRebateGroupsSummary(tableSearch)

  const refreshData = () => {
    forceRefreshTable()
    forceRefreshGroupsSummary()
  }

  const { createOrUpdateRebateGroup } = useCreateOrUpdateRebateGroup({
    onCompleted: refreshData,
  })

  const { deleteRebateGroup, isLoading: isDeleteRebateGroupLoading } = useDeleteRebateGroup({
    onCompleted: refreshData,
  })

  const isTableLoading = tableQuery.isLoading || isDeleteRebateGroupLoading

  const handlePageChange = (pageIndex: number, pageSize: number) => {
    tableQuery.setPageQuery?.({
      ...tableQuery.pageQuery,
      pageIndex,
      pageSize,
    })
  }

  const handleSubmit = async (values: Partial<GroupFormValues>, groupId?: string) => {
    if (
      values.name &&
      values.rebateAmountInPercentage &&
      values.rebateApprovalTypeId &&
      values.rebatePeriodId != null
    ) {
      await createOrUpdateRebateGroup(
        {
          name: values.name,
          rebateAmountInPercentage: values.rebateAmountInPercentage,
          rebateApprovalTypeId: values.rebateApprovalTypeId,
          rebatePeriodId: values.rebatePeriodId,
          isDefault: !!values.isDefault,
        },
        groupId
      )
    }

    refreshData()
    groupModal.close()
  }

  const deleteGroupConfirm = useModal(ConfirmModal, {
    title: t('Please, confirm your action'),
    description: t('IB.RebatesPage.GroupsPage.You are trying to delete the Group(s)'),
  })

  const groupModal = useModal(GroupModal, {
    approvalTypes: rebateApprovalTypes,
    rebatePeriods: rebatePeriods,
    onSubmit: handleSubmit,
  })

  const openCreateGroupModal = () => {
    groupModal.open({
      groupData: undefined,
      isCreateGroup: true,
    })
  }

  const onEdit = (group: IntroducingBrokerRebateGroupDto) => {
    groupModal.open({
      groupData: group,
      isCreateGroup: false,
    })
  }

  const onDeleteClick = (groupId: string) => {
    deleteGroupConfirm.open({
      onClose: () => deleteGroupConfirm.close(),
      onConfirm: async () => {
        await deleteRebateGroup(groupId)
        deleteGroupConfirm.close()
      },
    })
  }

  return (
    <div className={styles.groupsPage}>
      <TableSummary isLoading={isSummaryLoading} summaryItems={summaryItems} />

      <div className={styles.container}>
        <PageSection
          title={t('IB.RebatesPage.Groups')}
          id={ScrollToIds.IBRebateGroupsHeader}
          onFilterClick={() => setIsFilterVisible((prev) => !prev)}
          renderActions={[
            {
              icon: CirclePlusIcon,
              title: t('IB.RebatesPage.GroupsPage.Create new group'),
              onClick: openCreateGroupModal,
            },
          ]}
        />
        <TableFilter
          isVisible={isFilterVisible}
          searchFilters={tableSearch}
          groupSearchProps={{
            setSearch: (search) => search && setListSearch(search),
            options: groupOptions,
          }}
          filterOptions={[
            FilterOptions.IntroducingBrokerGroupId,
            FilterOptions.RebateApprovalType,
            FilterOptions.RebatePeriod,
            FilterOptions.TradingAccountCount,
            FilterOptions.Amount,
          ]}
          customLabels={{
            [FilterOptions.Amount]: t('IB.RebatesPage.Rebates'),
          }}
          customIcons={{
            [FilterOptions.Amount]: PercentIcon,
          }}
          rebatePeriods={rebatePeriods}
          approvalTypes={rebateApprovalTypes}
          setSearchFilters={(searchFilters) => {
            if (!searchFilters) return
            setTableSearch(searchFilters)
            tableQuery.setPageQuery?.({
              ...tableQuery.pageQuery,
              pageIndex: 1,
            })
          }}
        />
        <div className={styles.tableContainer}>
          <Loading isLoading={isTableLoading} showLoadingIcon>
            <GroupsPageTable
              tableQuery={tableQuery}
              isAnyFilterActive={isAnyFilterActive}
              openCreateGroupModal={openCreateGroupModal}
              sortOptions={tableSortOptions}
              sticky='left'
              onEdit={(group) => onEdit(group)}
              onDelete={(group) => onDeleteClick(group.id)}
            />

            {tableQuery.meta && tableQuery.hasInitialResults && (
              <Pagination
                scrollToHeaderId={ScrollToIds.IBRebateGroupsHeader}
                maxPageSize={100}
                pageData={tableQuery.meta}
                isLoading={tableQuery.isLoading}
                onPageChanged={handlePageChange}
              />
            )}
          </Loading>
        </div>
      </div>
    </div>
  )
}

interface IBRebateGroup {
  query: PaginatedQueryResult<IntroducingBrokerRebateGroupDto>
  sortOptions: NameDto<keyof IntroducingBrokerRebateGroupDto | null>[]
  search?: FilterQueryProps
  setSearch: (search: FilterQueryProps) => void
  forceRefresh: () => void
  clearSearch: (callback: () => void) => void
}

const useIBRebateGroups = (): IBRebateGroup => {
  const { t } = useTranslation()

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()
  const { account } = useAccountReadContext()

  const [search, setSearch] = useState<FilterQueryProps>()

  const sortOptions: NameDto<keyof IntroducingBrokerRebateGroupDto | null>[] = [
    { id: 'name', name: t('IB.RebatesPage.GroupsPage.Group name') },
    { id: 'rebateApprovalType', name: t('IB.RebatesPage.GroupsPage.Group type') },
    { id: 'rebatePeriod', name: t('IB.RebatesPage.GroupsPage.Rebate period') },
    { id: null, name: t('IB.RebatesPage.GroupsPage.TAs') },
    { id: 'rebateAmountInPercentage', name: t('IB.RebatesPage.Rebates') },
  ]

  const clearSearch = (callback: () => void) => {
    callback()
    setSearch({})
  }

  const ibId = account?.clientIntroducingBroker?.id ?? ''

  const query = usePaginatedQuery({
    queryKey: queryKeys.ib.rebates.groups(ibId, { search, locale }),
    queryFn: (pageQuery) =>
      apiClient.getIBRebateGroups(ibId, {
        ...pageQuery,
        search: processSearchQuery(ibId, search),
        caller: 'ca',
        languageId: locale,
      }),
  })

  return { query, sortOptions, search, setSearch, forceRefresh: query.forceRefresh, clearSearch }
}

interface IBRebateGroupsSummary {
  summaryItems: SummaryItem[]
  forceRefresh: () => void
  isLoading: boolean
}

const useIBRebateGroupsSummary = (search?: FilterQueryProps): IBRebateGroupsSummary => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const ibId = account?.clientIntroducingBroker?.id

  const { data: summaryData, isLoading } = useQuery({
    queryKey: queryKeys.ib.rebateGroupsSummary(ibId, search as Record<string, unknown>),
    queryFn: () =>
      apiClient.getIBRebateGroupsSummary(ibId!, { search: processSearchQuery(ibId || '', search) }),
    enabled: !!ibId,
  })

  const forceRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.ib.rebateGroupsSummary(ibId, search as Record<string, unknown>),
    })
  }, [queryClient, ibId, search])

  const summaryItems = useMemo<SummaryItem[]>(
    () => [
      { title: t('IB.RebatesPage.GroupsPage.Groups total'), value: summaryData?.groupsCount || 0 },
      { title: t('IB.RebatesPage.GroupsPage.TAs in groups'), value: summaryData?.tasInGroups || 0 },
      {
        title: t('IB.RebatesPage.GroupsPage.TAs with manual approval'),
        value: summaryData?.tasWithManualApproval || 0,
      },
      {
        title: t('IB.RebatesPage.GroupsPage.TAs not in a group'),
        value: summaryData?.tasNotInGroup || 0,
      },
    ],
    [summaryData, t]
  )

  return { summaryItems, forceRefresh, isLoading }
}

interface DeleteRebateGroup {
  isLoading: boolean
  deleteRebateGroup: (id: string) => Promise<void>
}
interface DeleteRebateGroupParams {
  onCompleted?: () => void
}

const useDeleteRebateGroup = ({ onCompleted }: DeleteRebateGroupParams = {}): DeleteRebateGroup => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { addSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState(false)

  const deleteRebateGroup = async (groupId: string) => {
    if (!account?.clientIntroducingBroker?.id) return

    setIsLoading(true)
    try {
      await apiClient.deleteRebateGroup(account.clientIntroducingBroker.id, groupId)
      addSnackbar.success({
        message: t('IB.RebatesPage.GroupsPage.You’ve successfully deleted a rebate group'),
      })
      setIsLoading(false)
      onCompleted?.()
    } catch (error: unknown) {
      console.error(error)
      setIsLoading(false)
    }
  }

  return { deleteRebateGroup, isLoading }
}

const processSearchQuery = (ibId: string, searchQuery?: FilterQueryProps) => {
  if (!searchQuery) return searchQuery

  const updatedSearchQuery = { ...searchQuery }

  Object.entries(updatedSearchQuery).forEach(([key, value]) => {
    if (CA_TRADING_ACCOUNTS_COUNT_FILTER_REGEX.test(key) && value) {
      updatedSearchQuery[key] = `${ibId}__${value}`
    }
  })

  return updatedSearchQuery
}
